import { handleApiResult } from '@store/error';
import { apiSlice } from './apiSlice';

/**
 * A user-authored label, keyed by locale code. Never rendered directly — resolve it
 * through `resolveLabel`, which shows the reader's own language and falls back to the
 * server's primary locale, so nothing ever renders blank.
 */
export type LocalizedText = Record<string, string>;

/** Mirrors the backend's `formInputTypes`, which in turn mirrors Composer's `FormInputType`. */
export const formInputTypes = ['Text', 'Number', 'Barcode', 'List', 'MultiList', 'Toggle', 'Range'] as const;
export type FormInputType = typeof formInputTypes[number];

export const barcodeFormats = ['QR', 'Code128', 'Code39', 'EAN13', 'DataMatrix'] as const;
export type BarcodeFormat = typeof barcodeFormats[number];

export interface PoiFieldOption {
  /** Absent only on an option that has never been saved; the server mints one. */
  id?: string;
  label: LocalizedText;
}

export interface PoiField {
  id?: string;
  label: LocalizedText;
  help?: string;
  type: FormInputType;
  required: boolean;
  readOnly: boolean;
  showOnReports: boolean;
  placeholder?: string;

  // Text
  multiline?: boolean;
  charLimit?: number;

  // Text, Barcode
  pattern?: string;

  // Number, Range
  decimal?: boolean;

  // Barcode
  formats?: BarcodeFormat[];

  // List, MultiList
  options?: PoiFieldOption[];

  // Range
  min?: number;
  max?: number;

  /**
   * What the field holds: the author's default here, the inspector's answer once Composer
   * has embedded this object into a checklist. Shaped by `type` — a string for Text,
   * Barcode and List (an option id), a number for Number and Range, a boolean for Toggle,
   * an array of option ids for MultiList.
   */
  value?: string | number | boolean | string[];
}

export interface PoiFieldGroup {
  id?: string;
  name: LocalizedText;
  description?: string;
  fields: PoiField[];
}

export interface PoiTypeShare {
  teamId: string;
  sharedAt?: string;
}

export interface PoiType {
  _id: string;
  teamId: string;
  myPermissions?: string[];
  name: LocalizedText;
  description?: string;
  /** Rises on every edit *and* whenever an ancestor changes. Sent back on save to detect a clash. */
  version: number;
  inheritFrom: string | null;
  sharedWith: PoiTypeShare[];
  /** Fields sitting directly on the type, outside any group. */
  fields: PoiField[];
  groups: PoiFieldGroup[];
  createdAt: string;
  updatedAt: string;
}

/** Where a resolved entry is defined, or null when it belongs to the type itself. */
export type InheritedFrom = { id: string; name: LocalizedText } | null;

/**
 * One entry of a resolved schema. Fields and groups share a single ordered list because
 * the order between them is the order a device renders in; `kind` discriminates. A group
 * still carries its own fields nested, so grouping survives.
 */
export type ResolvedItem =
  | { kind: 'field'; field: PoiField; inheritedFrom: InheritedFrom }
  | { kind: 'group'; group: PoiFieldGroup; inheritedFrom: InheritedFrom };

export interface EffectivePoiType {
  id: string;
  name: LocalizedText;
  version: number;
  /** Ancestors' content first, oldest first; within one type, its fields then its groups. */
  items: ResolvedItem[];
}

/** The whole schema, as `PUT` expects it. Anything omitted is removed, ids included. */
export type PoiTypeBody = Pick<PoiType, 'name' | 'description' | 'inheritFrom' | 'fields' | 'groups'>;

export type CreatePoiTypeInput = PoiTypeBody & { teamId: string };
export type UpdatePoiTypeInput = PoiTypeBody & { id: string; version?: number };

/**
 * Resolves a label for display: the reader's own interface language first, then the
 * server's primary locale, then anything that carries text.
 *
 * `preferred` is the language chosen in Settings — what the reader actually wants to see.
 * `primary` is only the fallback, and it is a total one: the backend requires every label
 * to carry it, so a label can never come out blank. The last step is a safety net for
 * data written before that rule, or straight into the database.
 *
 * A region-tagged interface language falls back to its base language too, so a reader on
 * `en-US` still matches a label written as `en`.
 */
export const resolveLabel = (
  text: LocalizedText | undefined,
  { primary, preferred }: { primary: string; preferred?: string }
): string => {
  if (!text) return '';

  const baseOf = (locale: string) => locale.split('-')[0];

  const candidates = [preferred, preferred && baseOf(preferred), primary, baseOf(primary)]
    .filter((locale): locale is string => Boolean(locale));

  for (const locale of candidates) {
    if (text[locale]?.trim()) return text[locale];
  }

  return Object.values(text).find((value) => value?.trim()) ?? '';
};

/**
 * Strips a field down to the keys its own input type accepts.
 *
 * The API validates each field against a strict per-type schema, so a `Number` carrying a
 * `formats` key is a 400 — and that is exactly what comes back from a document written
 * before the model stopped defaulting those arrays to `[]`. Every write path runs a field
 * through here, so an old document can still be edited.
 */
export const toFieldInput = (field: PoiField): PoiField => {
  const common = {
    id: field.id,
    label: field.label,
    help: field.help,
    type: field.type,
    required: field.required,
    readOnly: field.readOnly,
    showOnReports: field.showOnReports,
    placeholder: field.placeholder,
  };

  switch (field.type) {
    case 'Text':
      return {
        ...common, multiline: field.multiline, pattern: field.pattern, charLimit: field.charLimit,
        value: field.value,
      };
    case 'Number':
      return { ...common, decimal: field.decimal, value: field.value };
    case 'Barcode':
      return { ...common, formats: field.formats, pattern: field.pattern, value: field.value };
    case 'List':
    case 'MultiList':
      return { ...common, options: field.options, value: field.value };
    case 'Toggle':
      return { ...common, value: field.value ?? false };
    case 'Range':
      return { ...common, min: field.min, max: field.max, decimal: field.decimal, value: field.value };
  }
};

/** The same, for a whole type's worth of fields and groups. */
export const toPoiTypeBody = (body: PoiTypeBody): PoiTypeBody => ({
  ...body,
  fields: body.fields.map(toFieldInput),
  groups: body.groups.map((group) => ({ ...group, fields: group.fields.map(toFieldInput) })),
});

/**
 * Id for an entry the user has just added, minted here rather than waiting for the server.
 *
 * The API mints one for any entry that arrives without an id, but a choice needs its id
 * *before* the first save: defaults are stored as option ids, so an option with no id
 * could not be marked as the default until the field had been saved and read back.
 *
 * Not `crypto.randomUUID()` — that is only defined in a secure context, and this app is
 * served over plain HTTP. Ids only have to be unique inside one type, which this is.
 */
export const newEntityId = (): string =>
  `c_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

/**
 * Whether a label is fillable-in at all. This is the rule the API enforces: some locale
 * has to carry text. Which one is not required — the primary is the preferred fallback,
 * not a condition of saving.
 */
export const hasText = (text: LocalizedText | undefined): boolean =>
  Object.values(text ?? {}).some((value) => value?.trim());

/** How many of the enabled locales a label has been translated into. */
export const translatedCount = (text: LocalizedText | undefined, enabled: string[]): number =>
  enabled.filter((locale) => text?.[locale]?.trim()).length;

export const poiTypeApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPoiTypes: builder.query<PoiType[], void>({
      query: () => '/v1/poi-types',
      providesTags: (result) =>
        result
          ? [
            ...result.map(({ _id }) => ({ type: 'PoiType' as const, id: _id })),
            { type: 'PoiType', id: 'LIST' },
          ]
          : [{ type: 'PoiType', id: 'LIST' }],
      onQueryStarted: handleApiResult(),
    }),
    getEffectivePoiType: builder.query<EffectivePoiType, string>({
      query: (id) => `/v1/poi-types/${id}/effective`,
      providesTags: (_result, _error, id) => [{ type: 'PoiType', id }],
      onQueryStarted: handleApiResult(),
    }),
    addPoiType: builder.mutation<{ id: string }, CreatePoiTypeInput>({
      query: (body) => ({ url: '/v1/poi-types', method: 'POST', body }),
      invalidatesTags: [{ type: 'PoiType', id: 'LIST' }],
      onQueryStarted: handleApiResult('Api_PoiType_Add_Successful'),
    }),
    updatePoiType: builder.mutation<void, UpdatePoiTypeInput>({
      query: ({ id, ...body }) => ({ url: `/v1/poi-types/${id}`, method: 'PUT', body }),
      // Editing a type moves the version of everything that inherits from it, so the whole
      // list is refetched rather than just this row.
      invalidatesTags: ['PoiType'],
      onQueryStarted: handleApiResult('Api_PoiType_Update_Successful'),
    }),
    deletePoiType: builder.mutation<void, string>({
      query: (id) => ({ url: `/v1/poi-types/${id}`, method: 'DELETE' }),
      // Only the list — deliberately *not* this type's own tag. Invalidating that would
      // send the still-mounted `/effective` subscription back to the server for a type
      // that no longer exists, and its 404 surfaced as an error right after the delete
      // reported success. Nothing else needs refetching: a type other types inherit from
      // cannot be deleted at all, so no other version moves.
      invalidatesTags: [{ type: 'PoiType', id: 'LIST' }],
      onQueryStarted: handleApiResult('Api_PoiType_Delete_Successful'),
    }),
    sharePoiType: builder.mutation<void, { id: string; teamIds: string[] }>({
      query: ({ id, teamIds }) => ({ url: `/v1/poi-types/${id}/shares`, method: 'POST', body: { teamIds } }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'PoiType' as const, id }, { type: 'PoiType', id: 'LIST' }],
      onQueryStarted: handleApiResult('Api_PoiTypeShare_Add_Successful'),
    }),
    deletePoiTypeShare: builder.mutation<void, { id: string; teamId: string }>({
      query: ({ id, teamId }) => ({ url: `/v1/poi-types/${id}/shares/${teamId}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'PoiType' as const, id }, { type: 'PoiType', id: 'LIST' }],
      onQueryStarted: handleApiResult('Api_PoiTypeShare_Delete_Successful'),
    }),
  }),
});

export const {
  useGetPoiTypesQuery,
  useGetEffectivePoiTypeQuery,
  useAddPoiTypeMutation,
  useUpdatePoiTypeMutation,
  useDeletePoiTypeMutation,
  useSharePoiTypeMutation,
  useDeletePoiTypeShareMutation,
} = poiTypeApiSlice;
